import { useState, type ReactNode } from 'react';
import type { KeyedMutator } from 'swr';
import { useTranslation } from 'react-i18next';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { UpdateVehiclePayload, Vehicle } from '@/types';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import type { DirectoryItemAction } from '@/features/DirectoryItemPage/types';
import { MergeVehicleModal } from '@/features/MergeIdentity/vehicle/MergeVehicleModal';
import { AddVehicleModal } from '@/features/VehicleGrid/components/AddVehicleModal';
import { DeleteVehicleConfirmationModal } from '@/features/VehicleGrid/components/DeleteVehicleConfirmationModal';
import { useDuplicatePlateMerge } from '@/features/VehicleGrid/hooks/useDuplicatePlateMerge';
import { useVehicleActions } from '@/pages/VehiclePage/hooks/useVehicleActions';
import {
    getIdentityActionDescriptors,
    type IdentityActionKind,
} from './identityActionDescriptors';
import type { IdentityActionsResult } from './types';

interface UseVehicleIdentityActionsOptions {
    vehicle: Vehicle | undefined;
    includeDelete: boolean;
    mutateDetail: KeyedMutator<Vehicle> | undefined;
    onChanged: () => void;
    onDeleted: () => void;
    onMerged: (targetVehicleId: string) => void;
    onSourceStale: () => void;
}

export function useVehicleIdentityActions({
    vehicle,
    includeDelete,
    mutateDetail,
    onChanged,
    onDeleted,
    onMerged,
    onSourceStale,
}: UseVehicleIdentityActionsOptions): IdentityActionsResult {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

    const revalidateVehicles = () =>
        void revalidateByCacheKeyPrefix(CACHE_KEYS.ALL_VEHICLES);

    const { handleUpdateError, dialog: duplicatePlateDialog } =
        useDuplicatePlateMerge({
            onMerged: (result) => {
                revalidateVehicles();
                onMerged(result.targetVehicle.id);
            },
            onSourceStale: () => {
                revalidateVehicles();
                onSourceStale();
            },
        });

    const {
        isEditModalOpen,
        setIsEditModalOpen,
        isEditingVehicle,
        editVehicle,
        markAsUnknown,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isDeletingVehicle,
        deleteVehicle,
    } = useVehicleActions({
        onVehicleUpdated: (updatedFields) => {
            if (vehicle) {
                mutateDetail?.(
                    { ...vehicle, ...updatedFields },
                    { revalidate: false },
                );
            }
            onChanged();
        },
        onVehicleDeleted: () => {
            onChanged();
            onDeleted();
        },
        onDuplicatePlateConflict: handleUpdateError,
    });

    if (!vehicle) {
        return { actions: [], modals: null, openAction: async () => {} };
    }

    const openAction = async (kind: IdentityActionKind) => {
        if (kind === 'markAsUnknown') {
            await markAsUnknown(vehicle.id);
            return;
        }
        if (kind === 'merge') {
            setIsMergeModalOpen(true);
            return;
        }
        if (kind === 'delete') {
            setIsDeleteModalOpen(true);
            return;
        }
        setIsEditModalOpen(true);
    };

    const actions = getIdentityActionDescriptors({
        entityKind: 'vehicle',
        known: vehicle.known,
        includeDelete,
        t,
    }).map((descriptor) => ({
        icon: descriptor.icon,
        label: descriptor.label,
        onClick: () => void openAction(descriptor.kind),
    }));

    const modals = (
        <>
            <AddVehicleModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onUpdate={editVehicle}
                isSubmitting={isEditingVehicle}
                mode="edit"
                vehicle={vehicle}
            />
            <DeleteVehicleConfirmationModal
                isOpen={isDeleteModalOpen}
                deleteInProgress={isDeletingVehicle}
                onConfirm={() => deleteVehicle(vehicle.id)}
                onCancel={() => setIsDeleteModalOpen(false)}
            />
            {isMergeModalOpen && (
                <MergeVehicleModal
                    isOpen={isMergeModalOpen}
                    onClose={() => setIsMergeModalOpen(false)}
                    sourceVehicle={vehicle}
                    onSuccess={(result) => {
                        setIsMergeModalOpen(false);
                        revalidateVehicles();
                        onChanged();
                        onMerged(result.targetVehicle.id);
                    }}
                    onSourceStale={() => {
                        revalidateVehicles();
                        onSourceStale();
                    }}
                />
            )}
            {duplicatePlateDialog}
        </>
    );

    return { actions, modals, openAction };
}
